import type { SceneMeta, SceneRegion, ChoreographyCue } from '../../../../shared/SceneTypes';
import { presetForLevel } from '../../../../config/atmosphere/presets.config';

/** Minimal structural level input (PlatformerLevel-shaped) — keeps the resolver pure + decoupled. */
export interface SceneLevelInput {
  id: string;
  height?: number;
  platforms?: ReadonlyArray<{ x: number; y: number; width: number; height: number }>;
  scene?: SceneMeta;
}

/** Fully-resolved scene — the single contract every component reads (no optionals). */
export interface ResolvedScene {
  version: number;
  biome: string | null;
  groundY: number;
  horizonY: number;
  skyTopY: number;
  sceneMood: number;
  ambientTint: number | null;
  regions: SceneRegion[];
  cues: ChoreographyCue[];
}

/** Main-floor world-Y: the TOP (min-y wins ties) of the BROADEST platform; fallback level.height. */
export function deriveGroundY(level: SceneLevelInput): number {
  const ps = level.platforms ?? [];
  if (ps.length === 0) return level.height ?? 720;
  let best = ps[0];
  for (const p of ps) if (p.width > best.width || (p.width === best.width && p.y > best.y)) best = p;
  return best.y;
}

export function resolveScene(level: SceneLevelInput): ResolvedScene {
  const s = level.scene ?? {};
  const groundY = s.groundY ?? deriveGroundY(level);
  return {
    version: s.version ?? 1,
    biome: s.biome ?? presetForLevel(level.id)?.id ?? null,
    groundY,
    horizonY: s.horizonY ?? groundY,
    skyTopY: s.skyTopY ?? 0,
    sceneMood: s.sceneMood ?? 1,
    ambientTint: s.ambientTint ?? null,
    regions: s.regions ?? [],
    cues: s.cues ?? [],
  };
}
